import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, PencilLine, Download, Send } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";
import { useStripeMode } from "../../lib/useStripeMode";

const SUIVI_TASK_LABELS = { a_faire: "À faire", en_cours: "En cours", termine: "Terminée" };

const PAY_STATUS_LABELS = {
  en_attente: "En attente de paiement", paye: "Payé", echoue: "Échoué", expire: "Expirée",
  remplacee: "Remplacée", rembourse_partiel: "Remboursé partiellement", rembourse: "Remboursé",
  enregistre: "Enregistré (simulation historique)",
  empreinte_en_cours: "Empreinte en cours", empreinte_active: "Empreinte active (non débitée)",
  liberee: "Libérée", capturee: "Retenue totale", capturee_partielle: "Retenue partielle",
  derogee: "Dérogation Art Event", doublon_annule: "Doublon annulé", expiree: "Expirée",
};
const PAY_TYPE_LABELS = { acompte: "Acompte 30 %", solde: "Solde", complement: "Complément", caution: "Caution (empreinte)" };

const RDV_SLOTS = ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00", "17:00"];
const RDV_MOTIFS = [
  { id: "location", label: "Location & décoration" },
  { id: "coordination", label: "Coordination mariage" },
  { id: "decouverte", label: "Découverte" },
  { id: "telephone_visio", label: "Téléphone / visio" },
];

async function openDocument(d, view) {
  try {
    const r = await api.get(`/client/documents/${d.id}/file`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    if (view) {
      window.open(url, "_blank", "noopener");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(d.label || "document").replace(/[^\w\- ]/g, "")}.pdf`;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  } catch {
    toast.error("Document indisponible pour le moment");
  }
}

function ReservationPayments({ reservation: r, onChanged }) {
  const [data, setData] = useState(null);
  const loadPayments = () => api.get(`/client/reservations/${r.id}/payments`).then((res) => setData(res.data)).catch(() => setData(false));
  useEffect(() => { loadPayments(); }, [r.id]);

  const goStripe = async (path, testid) => {
    try {
      const res = await api.post(`/client/reservations/${r.id}/${path}`, { origin_url: window.location.origin });
      if (res.data.checkout_url) {
        toast.success("Redirection vers la page sécurisée Stripe…");
        window.location.href = res.data.checkout_url;
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (data === null) return <div className="card-ae p-6 text-sm text-muted-foreground">Chargement…</div>;
  if (data === false) return null;
  const dep = data.deposit;

  return (
    <div className="card-ae p-6" data-testid={`payments-panel-${r.ref}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-heading text-lg text-deepgreen">{r.ref}</p>
        <StatusBadge status={r.status} />
      </div>
      <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
        <p><span className="text-muted-foreground">Total :</span> <strong>{(r.totals?.total_ttc ?? 0).toFixed(2)} €</strong></p>
        <p><span className="text-muted-foreground">Reste à régler :</span> <strong data-testid={`remaining-due-${r.ref}`}>{(data.remaining_due ?? 0).toFixed(2)} €</strong></p>
        <p><span className="text-muted-foreground">Échéance solde :</span> au plus tard J-7</p>
      </div>
      {r.status === "confirmee" && data.remaining_due > 0 && (
        <button type="button" onClick={() => goStripe("pay-balance")} className="btn-primary-ae mt-4 !px-5 !py-2.5 !text-xs" data-testid={`pay-balance-${r.ref}`}>
          Régler le solde ({(data.remaining_due ?? 0).toFixed(2)} €) — payable à tout moment
        </button>
      )}
      {r.status === "confirmee" && data.remaining_due <= 0 && (
        <p className="mt-4 rounded-lg bg-sage-light px-4 py-2.5 text-xs font-bold text-deepgreen" data-testid={`fully-paid-${r.ref}`}>Réservation soldée — merci !</p>
      )}

      {dep && (
        <div className="mt-4 rounded-lg border-2 border-dashed border-sage bg-cream p-4" data-testid={`deposit-block-${r.ref}`}>
          <p className="flex items-center gap-2 text-sm font-bold text-deepgreen">
            Caution — {dep.amount?.toFixed(2)} € <span className="text-xs font-normal text-muted-foreground">(empreinte bancaire, séparée du paiement, jamais débitée automatiquement)</span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground" data-testid={`deposit-status-${r.ref}`}>
            Statut : {PAY_STATUS_LABELS[dep.status] || dep.status}
            {dep.capture_before ? ` — valable jusqu'au ${String(dep.capture_before).slice(0, 10)}` : ""}
          </p>
          {dep.status === "en_attente" && !dep.can_request && dep.available_from && (
            <p className="mt-2 text-xs font-semibold text-deepgreen">Disponible à partir du {dep.available_from} (J-5 avant l'événement) — vous serez notifié par e-mail.</p>
          )}
          {dep.can_request && (
            <button type="button" onClick={() => goStripe("deposit")} className="btn-secondary-ae mt-3 !px-5 !py-2.5 !text-xs" data-testid={`deposit-start-${r.ref}`}>
              Réaliser l'empreinte de caution (aucun débit)
            </button>
          )}
          {dep.status === "empreinte_en_cours" && (
            <p className="mt-2 text-xs font-semibold text-deepgreen">Empreinte en cours — finalisez-la sur la page sécurisée ouverte, ou relancez-la.</p>
          )}
        </div>
      )}

      <div className="mt-5">
        <p className="label-ae">Historique des transactions</p>
        {data.payments.length === 0 ? (
          <p className="mt-2 text-xs text-muted-foreground">Aucune transaction pour le moment.</p>
        ) : (
          <ul className="mt-2 space-y-2" data-testid={`payments-history-${r.ref}`}>
            {data.payments.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-cream px-4 py-2.5 text-sm" data-testid={`payment-row-${p.id}`}>
                <span className="font-semibold text-deepgreen">{PAY_TYPE_LABELS[p.type] || p.type}</span>
                <span>{(p.amount ?? 0).toFixed(2)} €{p.refunded_amount > 0 ? ` (remboursé ${p.refunded_amount.toFixed(2)} €)` : ""}</span>
                <StatusBadge status={PAY_STATUS_LABELS[p.status] || p.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function SuiviMariage({ eventId }) {
  const [dossier, setDossier] = useState(undefined);
  useEffect(() => {
    api.get("/client/coordination", { params: { event_id: eventId } })
      .then((r) => setDossier(r.data))
      .catch(() => setDossier(null));
  }, [eventId]);

  if (dossier === undefined) return <p className="text-sm text-muted-foreground">Chargement…</p>;
  if (dossier === null) return <EmptyState title="Dossier Coordination en préparation">Art Event finalise votre dossier — il apparaîtra ici.</EmptyState>;

  const { progression } = dossier;
  const pct = progression.total > 0 ? Math.round((progression.done / progression.total) * 100) : 0;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-heading text-lg text-deepgreen">Suivi du mariage — {dossier.formule_name}</h3>
        <span className="rounded-full bg-gold-light px-3 py-1 text-xs font-bold text-deepgreen">
          {dossier.day_j ? "Présence jour J jusqu'au dessert" : "Sans présence jour J"}
        </span>
      </div>
      <div className="mt-4" data-testid="suivi-progression">
        <div className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
          <span>Progression du dossier</span><span>{progression.done}/{progression.total} éléments — {pct} %</span>
        </div>
        <div className="mt-2 h-2.5 rounded-full bg-cream-dark">
          <div className="h-2.5 rounded-full bg-sage transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>
      {dossier.tasks.length > 0 && (
        <div className="mt-5">
          <p className="label-ae">Éléments à préparer</p>
          <ul className="space-y-2">
            {dossier.tasks.map((t) => (
              <li key={t.id} className="flex items-center justify-between rounded-lg bg-cream px-4 py-3 text-sm" data-testid={`suivi-task-${t.id}`}>
                <span className={t.status === "termine" ? "text-muted-foreground line-through" : "text-deepgreen font-semibold"}>{t.title}</span>
                <span className="text-xs text-muted-foreground">{t.due_date || ""} · {SUIVI_TASK_LABELS[t.status]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {dossier.vendors.length > 0 && (
        <div className="mt-5">
          <p className="label-ae">Prestataires</p>
          <ul className="space-y-2">
            {dossier.vendors.map((v) => (
              <li key={v.id} className="flex items-center justify-between rounded-lg bg-cream px-4 py-3 text-sm" data-testid={`suivi-vendor-${v.id}`}>
                <span className="font-semibold text-deepgreen">{v.name} <span className="text-xs font-normal text-muted-foreground">— {v.category}</span></span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {dossier.appointments.length > 0 && (
        <div className="mt-5">
          <p className="label-ae">Prochains rendez-vous</p>
          <ul className="space-y-2">
            {dossier.appointments.map((a) => (
              <li key={a.id} className="rounded-lg bg-cream px-4 py-3 text-sm text-deepgreen" data-testid={`suivi-rdv-${a.id}`}>{a.date} — {a.slot}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="mt-5 text-xs text-muted-foreground">Seuls les éléments visibles sont affichés — les notes internes Art Event restent confidentielles.</p>
    </div>
  );
}

export default function EventDetailPage() {
  const { eventId } = useParams();
  const stripeMode = useStripeMode();
  const [evt, setEvt] = useState(null);
  const [tab, setTab] = useState("apercu");
  const [quotes, setQuotes] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [thread, setThread] = useState(null);
  const [messageDraft, setMessageDraft] = useState("");
  const [terms, setTerms] = useState(null);
  const [rdvForm, setRdvForm] = useState(null);
  const [rdvSending, setRdvSending] = useState(false);
  const load = () => {
    api.get(`/client/events/${eventId}`).then((r) => setEvt(r.data)).catch(() => setEvt(false));
    api.get("/client/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    api.get("/client/appointments").then((r) => setAppointments(r.data)).catch(() => {});
    api.get("/client/documents", { params: { event_id: eventId } }).then((r) => setDocuments(r.data)).catch(() => {});
    api.get("/terms/current").then((r) => setTerms(r.data)).catch(() => setTerms(null));
    api.get("/client/messages/threads").then(async (r) => {
      const linked = r.data.find((t) => t.event_id === eventId) || r.data[0];
      if (linked) {
        const full = await api.get(`/client/messages/threads/${linked.id}`);
        setThread(full.data);
      } else {
        setThread(null);
      }
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [eventId]);
  if (evt === null) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="event-loading">Chargement…</div>;
  if (evt === false) return <EmptyState title="Événement introuvable" />;

  const TABS = [
    { id: "apercu", label: "Vue d'ensemble" },
    { id: "reservations", label: "Réservations" },
    { id: "devis", label: "Devis" },
    { id: "paiements", label: "Paiements" },
    { id: "documents", label: "Documents" },
    { id: "messages", label: "Messages" },
    { id: "rdv", label: "Rendez-vous" },
    // « Suivi du mariage » uniquement si une prestation Coordination est réellement souscrite
    ...(evt.has_coordination ? [{ id: "suivi", label: "Suivi du mariage" }] : []),
  ];

  return (
    <div data-testid="event-detail-page">
      <Link to="/compte" className="inline-flex items-center gap-2 text-sm font-semibold text-deepgreen hover:text-sage" data-testid="event-back">
        <ArrowLeft className="h-4 w-4" /> Mes événements
      </Link>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl text-deepgreen" data-testid="event-title">{evt.title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{evt.date_start}{evt.date_end && evt.date_end !== evt.date_start ? ` → ${evt.date_end}` : ""} — {evt.venue_address || "Lieu à préciser"}</p>
        </div>
        <StatusBadge status={evt.status} />
      </div>

      <div className="mt-6 flex gap-1 overflow-x-auto border-b border-border" data-testid="event-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            data-testid={`event-tab-${t.id}`}
            className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${tab === t.id ? "border-deepgreen text-deepgreen" : "border-transparent text-muted-foreground hover:text-deepgreen"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "apercu" && (
          <div className="grid gap-6 md:grid-cols-2" data-testid="event-overview">
            <div className="card-ae p-6">
              <h3 className="font-heading text-lg text-deepgreen">Prestations souscrites</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-sage-light px-3 py-1 text-xs font-semibold text-deepgreen">Location de matériel</span>
                {evt.has_coordination && <span className="rounded-full bg-gold-light px-3 py-1 text-xs font-semibold text-deepgreen">Coordination de mariage</span>}
              </div>
              <p className="mt-4 text-sm text-muted-foreground">Location et coordination restent deux prestations indépendantes.</p>
            </div>
            <div className="card-ae p-6">
              <h3 className="font-heading text-lg text-deepgreen">Prochaine étape</h3>
              <p className="mt-2 text-sm text-muted-foreground">Solde à régler au plus tard J-7 avant l'événement.</p>
            </div>
          </div>
        )}
        {tab === "reservations" && (
          <div className="space-y-4" data-testid="event-reservations">
            {evt.reservations.map((r) => (
              <div key={r.ref} className="card-ae flex flex-wrap items-center justify-between gap-4 p-6" data-testid={`reservation-${r.ref}`}>
                <div>
                  <Link to={`/compte/reservations/${r.id}`} className="font-heading text-lg text-deepgreen underline-offset-4 hover:text-sage hover:underline" data-testid={`open-reservation-${r.ref}`}>{r.ref}</Link>
                  <StatusBadge status={r.status} />
                </div>
                <div className="text-right text-sm">
                  {r.promo_snapshot && <p className="font-semibold text-sage" data-testid={`promo-applied-${r.ref}`}>Code {r.promo_snapshot.code} — remise {(r.promo_snapshot.discount_amount ?? 0).toFixed(2)} €</p>}
                  <p className="font-semibold text-deepgreen">{(r.totals?.total_ttc ?? 0).toFixed(2)} € TTC</p>
                  <p className="text-muted-foreground">Acompte {(r.totals?.acompte ?? 0).toFixed(2)} € — Solde {(r.totals?.solde ?? 0).toFixed(2)} €</p>
                </div>
                {r.status === "en_attente_finalisation" && terms && (
                  <div className="w-full rounded-lg border border-sage bg-cream p-4 text-sm" data-testid="event-terms-current">
                    <p className="font-semibold text-deepgreen">CGV applicables : version {terms.version} — en vigueur depuis le {terms.effective_at}</p>
                    <details className="mt-2 text-xs text-muted-foreground"><summary className="cursor-pointer font-semibold text-deepgreen">Consulter les CGV</summary><div className="mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap rounded bg-white p-3">{terms.content}</div></details>
                  </div>
                )}
                {r.status === "en_attente_finalisation" && (
                  <button type="button" onClick={async () => {
                    try {
                      const res = await api.post(`/client/reservations/${r.id}/confirm`, { accept_terms: true, idempotency_key: crypto.randomUUID(), origin_url: window.location.origin });
                      if (res.data.checkout_url) {
                        toast.success("CGV acceptées — redirection vers le paiement sécurisé de l'acompte…");
                        window.location.href = res.data.checkout_url;
                      } else { toast.success("Réservation confirmée"); load(); }
                    }
                    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid={`confirm-reservation-${r.ref}`}>
                    Accepter les CGV & régler l'acompte (30 %)
                  </button>
                )}
                <button type="button" onClick={async () => {
                  const message = window.prompt("Décrivez la modification souhaitée :");
                  if (!message || message.length < 5) return;
                  try { await api.post(`/client/reservations/${r.id}/request-modification`, { message }); toast.success("Demande de modification envoyée à Art Event"); load(); }
                  catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                }} className="btn-secondary-ae !px-5 !py-2.5 !text-xs" data-testid={`request-modification-${r.ref}`}>
                  <PencilLine className="h-4 w-4" /> Demander une modification
                </button>
              </div>
            ))}
            <p className="text-xs text-muted-foreground">Vous pouvez demander une modification — Art Event la traite et crée un avenant. Une réservation confirmée n'est jamais modifiée directement.</p>
          </div>
        )}
        {tab === "paiements" && (
          <div className="space-y-4" data-testid="event-payments">
            {evt.reservations.length === 0 && <EmptyState title="Aucune réservation">Vos paiements apparaîtront ici dès votre première réservation.</EmptyState>}
            {evt.reservations.map((r) => <ReservationPayments key={r.ref} reservation={r} onChanged={load} />)}
            <p className="text-xs text-muted-foreground">
              {stripeMode === "stripe_test" ? "Paiements sécurisés par Stripe (mode TEST — aucune charge réelle). " : "Paiements sécurisés par Stripe. "}
              La caution est une empreinte bancaire séparée, jamais débitée automatiquement. Art Event ne conserve aucune donnée bancaire.
            </p>
          </div>
        )}
        {tab === "devis" && (
          <div className="space-y-4" data-testid="event-quotes">
            {quotes.length === 0 && <EmptyState title="Aucun devis">Les devis préparés par Art Event apparaîtront ici.</EmptyState>}
            {quotes.map((q) => (
              <div key={q.id} className="card-ae p-6" data-testid={`client-quote-${q.ref}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-heading text-lg text-deepgreen">{q.ref} <span className="text-xs text-muted-foreground">version {q.version}</span></p>
                    <p className="text-xs text-muted-foreground">Valable jusqu'au {(q.valid_until || "").slice(0, 10)}</p>
                  </div>
                  <StatusBadge status={{ brouillon: "Brouillon", envoye: "Nouvelle demande", accepte: "Confirmée", refuse: "Refusée", expire: "Passée" }[q.status] || q.status} />
                </div>
                <div className="mt-3 space-y-1.5 text-sm">
                  {q.items.map((i, idx) => (
                    <p key={idx} className="flex justify-between"><span className="text-muted-foreground">{i.name} × {i.qty}</span><span className="font-semibold">{(i.unit_price * i.qty).toFixed(2)} €</span></p>
                  ))}
                  <p className="flex justify-between border-t border-border pt-2"><span className="font-bold text-deepgreen">Total TTC</span><span className="font-bold text-deepgreen">{(q.totals?.total_ttc ?? 0).toFixed(2)} €</span></p>
                  <p className="flex justify-between"><span className="text-muted-foreground">Acompte 30 %</span><span className="font-semibold text-deepgreen">{(q.totals?.acompte ?? 0).toFixed(2)} €</span></p>
                </div>
                <div className="mt-3">
                  <button type="button" onClick={async () => { try { const r = await api.get(`/client/quotes/${q.id}/pdf`, { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `Devis_${q.ref}_v${q.version}.pdf`; a.click(); URL.revokeObjectURL(url); } catch { toast.error("PDF indisponible"); } }} className="btn-secondary-ae !px-4 !py-2 !text-[11px]" data-testid={`quote-pdf-${q.ref}`}>
                    Télécharger le PDF (v{q.version})
                  </button>
                </div>
                {q.status === "envoye" && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    <button type="button" onClick={async () => { try { await api.post(`/client/quotes/${q.id}/accept`); toast.success("Devis accepté — Art Event peut maintenant le transformer en réservation"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } }} className="btn-primary-ae !px-5 !py-2.5 !text-xs" data-testid={`quote-accept-${q.ref}`}>
                      Accepter le devis
                    </button>
                    <button type="button" onClick={async () => { const reason = window.prompt("Motif du refus (facultatif) :") || null; try { await api.post(`/client/quotes/${q.id}/refuse`, { reason }); toast.success("Devis refusé — Art Event en est informé"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } }} className="btn-secondary-ae !px-5 !py-2.5 !text-xs" data-testid={`quote-refuse-${q.ref}`}>
                      Refuser
                    </button>
                  </div>
                )}
                {q.status === "accepte" && <p className="mt-3 text-xs text-muted-foreground">Devis accepté — la réservation sera créée par Art Event, puis finalisée par vos soins (CGV + acompte).</p>}
              </div>
            ))}
          </div>
        )}
        {tab === "documents" && (
          <div className="card-ae p-6" data-testid="event-documents">
            <h3 className="font-heading text-lg text-deepgreen">Documents</h3>
            <p className="mt-1 text-xs text-muted-foreground">Devis, récapitulatifs et CGV acceptées (version figée, preuve conservée) — rattachés à cet événement.</p>
            {documents.length === 0 ? (
              <div className="mt-4"><EmptyState title="Aucun document pour cet événement">Les documents apparaîtront ici automatiquement (devis envoyé, récapitulatif après confirmation, CGV acceptées).</EmptyState></div>
            ) : (
              <ul className="mt-4 space-y-2" data-testid="event-documents-list">
                {documents.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-cream px-4 py-3" data-testid={`event-document-${d.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-deepgreen">{d.label}</p>
                      <p className="text-[11px] text-muted-foreground">{(d.created_at || "").slice(0, 10)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-white px-3 py-1 text-xs text-muted-foreground">{d.kind}</span>
                      <button type="button" onClick={() => openDocument(d, true)} className="btn-secondary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid={`doc-view-${d.id}`}>Voir</button>
                      <button type="button" onClick={() => openDocument(d, false)} className="btn-primary-ae !px-3.5 !py-1.5 !text-[10px]" data-testid={`doc-download-${d.id}`}>
                        <Download className="h-3.5 w-3.5" /> Télécharger
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {tab === "messages" && (
          <div className="card-ae p-6" data-testid="event-messages">
            <h3 className="font-heading text-lg text-deepgreen">Messages</h3>
            {thread && thread.messages?.length > 0 ? (
              <div className="mt-4 max-h-80 space-y-3 overflow-y-auto" data-testid="client-thread-messages">
                {thread.messages.map((m) => (
                  <div key={m.id} className={m.author === "client" ? "ml-auto max-w-md rounded-xl rounded-tr-none bg-deepgreen p-4 text-sm text-cream" : "max-w-md rounded-xl rounded-tl-none bg-cream p-4 text-sm text-foreground/85"}>
                    {m.body}
                    <p className={`mt-2 text-[10px] ${m.author === "client" ? "text-cream/60" : "text-muted-foreground"}`}>{m.author === "client" ? "Vous" : "Art Event"} — {(m.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4">
                <EmptyState title="Aucun message pour le moment">Votre premier message créera une conversation rattachée à cet événement.</EmptyState>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <input className="input-ae" placeholder="Écrire à Art Event…" value={messageDraft} onChange={(e) => setMessageDraft(e.target.value)} data-testid="event-message-input" />
              <button type="button" onClick={async () => {
                if (!messageDraft.trim()) return;
                try {
                  if (thread) {
                    await api.post(`/client/messages/threads/${thread.id}/reply`, { body: messageDraft });
                  } else {
                    await api.post("/client/messages/threads", { subject: `Événement ${evt.title || ""}`.trim() || "Question", body: messageDraft, event_id: eventId });
                  }
                  setMessageDraft("");
                  toast.success("Message envoyé à Art Event");
                  load();
                } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
              }} className="btn-primary-ae !px-5" data-testid="event-message-send" aria-label="Envoyer"><Send className="h-4 w-4" /></button>
            </div>
          </div>
        )}
        {tab === "rdv" && (
          <div className="card-ae p-6" data-testid="event-appointments">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-heading text-lg text-deepgreen">Rendez-vous</h3>
              <button type="button" onClick={() => setRdvForm((v) => v ? null : { type: "location", date: "", slot: RDV_SLOTS[0], message: "" })} className="btn-primary-ae !px-4 !py-2 !text-[11px]" data-testid="rdv-request-open">
                Demander un rendez-vous
              </button>
            </div>
            {rdvForm && (
              <form
                className="mt-4 space-y-3 rounded-lg border border-sage bg-cream p-4"
                data-testid="rdv-request-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  setRdvSending(true);
                  try {
                    await api.post("/client/appointments", { ...rdvForm, message: rdvForm.message || null, event_id: eventId });
                    toast.success("Demande envoyée — Art Event vous confirmera le créneau");
                    setRdvForm(null);
                    load();
                  } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  finally { setRdvSending(false); }
                }}
              >
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="label-ae" htmlFor="rdv-type">Motif</label>
                    <select id="rdv-type" className="input-ae" value={rdvForm.type} onChange={(e) => setRdvForm({ ...rdvForm, type: e.target.value })} data-testid="rdv-request-type">
                      {RDV_MOTIFS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-ae" htmlFor="rdv-date">Date souhaitée</label>
                    <input id="rdv-date" type="date" required min={new Date().toISOString().slice(0, 10)} className="input-ae" value={rdvForm.date} onChange={(e) => setRdvForm({ ...rdvForm, date: e.target.value })} data-testid="rdv-request-date" />
                  </div>
                  <div>
                    <label className="label-ae" htmlFor="rdv-slot">Créneau</label>
                    <select id="rdv-slot" className="input-ae" value={rdvForm.slot} onChange={(e) => setRdvForm({ ...rdvForm, slot: e.target.value })} data-testid="rdv-request-slot">
                      {RDV_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <label className="label-ae" htmlFor="rdv-msg">Commentaire (facultatif)</label>
                <textarea id="rdv-msg" className="input-ae min-h-16" maxLength={2000} value={rdvForm.message} onChange={(e) => setRdvForm({ ...rdvForm, message: e.target.value })} data-testid="rdv-request-message" />
                <button type="submit" disabled={rdvSending} className="btn-primary-ae !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid="rdv-request-submit">
                  {rdvSending ? "Envoi…" : "Envoyer la demande"}
                </button>
              </form>
            )}
            {appointments.length === 0 ? (
              <div className="mt-4"><EmptyState title="Aucun rendez-vous">Vos rendez-vous avec Art Event apparaîtront ici (agenda géré par Art Event).</EmptyState></div>
            ) : (
              <ul className="mt-4 space-y-2">
                {appointments.map((a) => (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-cream px-4 py-3" data-testid={`client-appointment-${a.id}`}>
                    <p className="text-sm font-semibold text-deepgreen">{a.date} — {a.slot}</p>
                    <StatusBadge status={{ demande: "Nouvelle demande", planifie: "En attente", confirme: "Confirmée", effectue: "Passée" }[a.status] || a.status} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {tab === "suivi" && evt.has_coordination && (
          <div className="card-ae p-6" data-testid="event-wedding-tracking">
            <SuiviMariage eventId={eventId} />
          </div>
        )}
      </div>
    </div>
  );
}
